import React from "react";
import styled from "styled-components";
import useKey from "../../hooks/useKey";
import { TASKBAR_HEIGHT } from "../Taskbar";

// The Start menu: raised panel anchored above the Start button with the
// classic blue sidebar banner. Items are provided by the Stage:
//   { label, icon, onClick?, disabled?, submenu?: [items], sceneTarget? }
//   — or the string "separator".
// Items with `submenu` open a cascading panel on hover (Programs ▸).
// `sceneTarget` stamps data-scene-target for the boot scene's ghost
// cursor to find. Escape closes; outside clicks are handled by the Stage.

function MenuItem({ item, onClose, depth = 0, forceSubmenuFor, forceHoverFor }) {
  const [subOpen, setSubOpen] = React.useState(false);
  if (item === "separator") return <Separator />;
  const hasSub = Array.isArray(item.submenu);
  // The boot scene's ghost cursor can't hover — the Stage can force a
  // submenu open (and its hover highlight) by scene target instead.
  const showSub = hasSub && (subOpen || forceSubmenuFor === item.label);
  const forcedHover =
    item.sceneTarget != null && forceHoverFor === item.sceneTarget;
  return (
    <ItemWrap
      onMouseEnter={() => hasSub && setSubOpen(true)}
      onMouseLeave={() => hasSub && setSubOpen(false)}
    >
      <Item
        type="button"
        role="menuitem"
        disabled={item.disabled}
        data-scene-target={item.sceneTarget}
        data-top={depth === 0 ? "true" : undefined}
        data-forced-hover={forcedHover ? "true" : undefined}
        data-sub-open={showSub ? "true" : undefined}
        onClick={() => {
          if (hasSub) {
            // Open, never toggle: iOS fires mouseenter+click on the same
            // tap, so a toggle would re-close what the enter just opened.
            setSubOpen(true);
            return;
          }
          if (onClose) onClose();
          if (item.onClick) item.onClick();
        }}
      >
        {/* Top-level menu uses 32px icons in ~32px rows; submenus use 16px. */}
        <ItemIcon
          aria-hidden="true"
          data-big={depth === 0 ? "true" : undefined}
        >
          {item.icon && depth === 0
            ? React.cloneElement(item.icon, { size: 32 })
            : item.icon}
        </ItemIcon>
        <ItemLabel>{item.label}</ItemLabel>
        {hasSub && <SubArrow aria-hidden="true">▸</SubArrow>}
      </Item>
      {showSub && (
        <SubPanel className="window" $depth={depth}>
          {item.submenu.map((sub, i) => (
            <MenuItem
              key={i}
              item={sub}
              onClose={onClose}
              depth={depth + 1}
              forceHoverFor={forceHoverFor}
            />
          ))}
        </SubPanel>
      )}
    </ItemWrap>
  );
}

function StartMenu({
  open,
  items = [],
  onClose,
  banner = "Solitaire",
  forceSubmenuFor,
  forceHoverFor,
}) {
  useKey("Escape", () => open && onClose && onClose());
  const panelRef = React.useRef(null);

  // Arrow keys walk the menu via roving focus over visible menuitems;
  // Enter activates natively since they're real buttons.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const panel = panelRef.current;
      if (!panel) return;
      e.preventDefault();
      const items_ = Array.from(
        panel.querySelectorAll('[role="menuitem"]:not(:disabled)'),
      );
      if (items_.length === 0) return;
      const idx = items_.indexOf(document.activeElement);
      const delta = e.key === "ArrowDown" ? 1 : -1;
      const next =
        idx < 0
          ? delta > 0
            ? 0
            : items_.length - 1
          : (idx + delta + items_.length) % items_.length;
      items_[next].focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!open) return null;
  return (
    <Panel
      ref={panelRef}
      className="window"
      role="menu"
      // Keep desktop-level outside-click handlers from seeing clicks
      // inside the menu.
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Banner aria-hidden="true">
        <span>{banner}</span>
      </Banner>
      <Items>
        {items.map((item, i) => (
          <MenuItem
            key={i}
            item={item}
            onClose={onClose}
            forceSubmenuFor={forceSubmenuFor}
            forceHoverFor={forceHoverFor}
          />
        ))}
      </Items>
    </Panel>
  );
}

const Panel = styled.div`
  position: absolute;
  left: 2px;
  bottom: ${TASKBAR_HEIGHT}px;
  z-index: 1100;
  display: flex;
  flex-direction: row;
  padding: 3px;
  min-width: 220px;
`;

const Banner = styled.div`
  width: 26px;
  flex-shrink: 0;
  align-self: stretch;
  background: linear-gradient(180deg, #00007b 0%, #0000c4 100%);
  position: relative;

  & span {
    position: absolute;
    bottom: 6px;
    left: 50%;
    transform: rotate(180deg) translateX(50%);
    transform-origin: center;
    writing-mode: vertical-rl;
    color: #fff;
    font-weight: bold;
    /* 11px (integer only — pixel font): the full title fits the menu's
       height at 11px; larger sizes overflow the panel top. */
    font-size: 11px;
    letter-spacing: 1px;
    white-space: nowrap;
    -webkit-user-select: none;
    user-select: none;
  }
`;

const Items = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding: 2px 0;
`;

const ItemWrap = styled.div`
  position: relative;
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: transparent;
  border: none;
  box-shadow: none;
  min-height: 0;
  min-width: 0;
  padding: 3px 14px 3px 7px;
  text-align: left;
  font: inherit;
  color: inherit;
  text-shadow: none;
  white-space: nowrap;

  /* 32px pitch at the top level, ~22px in cascades; the icons establish
     both heights. */
  &&[data-top="true"] {
    padding: 0 14px 0 4px;
  }

  &&:not(:disabled):hover,
  &&[data-ghost-hover="true"],
  &&[data-forced-hover="true"],
  &&[data-sub-open="true"] {
    background: #00007b;
    color: #fff;
    box-shadow: none;
  }
  &&:not(:disabled):active {
    box-shadow: none;
    text-shadow: none;
  }
  &&:focus {
    outline: none;
  }
  /* Keyboard focus reads as hover, like the real menus. */
  &&:focus-visible {
    background: #00007b;
    color: #fff;
  }
  &&:disabled {
    color: #808080;
  }
`;

const ItemIcon = styled.span`
  width: 20px;
  display: inline-flex;
  justify-content: center;
  flex-shrink: 0;

  &[data-big="true"] {
    width: 32px;
  }
`;

const ItemLabel = styled.span`
  flex: 1 1 auto;
`;

const SubArrow = styled.span`
  margin-left: 12px;
`;

const SubPanel = styled.div`
  position: absolute;
  left: calc(100% - 3px);
  top: -3px;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  padding: 3px 0;
  z-index: 1;
`;

const Separator = styled.div`
  height: 2px;
  margin: 3px 6px;
  border-top: 1px solid #808080;
  border-bottom: 1px solid #fff;
`;

export default StartMenu;
